import React, { useCallback } from 'react';
import {
  makeStyles,
  TableBody,
  TableCell,
  Typography
} from '@material-ui/core';
import clsx from 'clsx';
import { Inventory, ManufacturerOption } from 'src/types';
import { slices, useAppSelector } from 'src/redux';
import {
  formatCurrency,
  formatDate,
  getStatusColor,
  manufacturerIDToName,
  copyToClipboard
} from 'src/utils';
import { ProductTypeEnum } from 'src/enums';
import { AlternatingColorTableRow, LinkComponent } from 'src/components';
import { usePermissions } from 'src/hooks';
import FileCopyIcon from '@material-ui/icons/FileCopy';

const { selectors: inventorySelectors } = slices.inventory;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  inventoryCard: {
    height: '100%'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  tableRow: {
    padding: 10
  },
  tableCell: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  productNameCell: {
    maxHeight: '3.4em',
    verticalAlign: 'middle'
  },
  productNameText: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    lineHeight: '1.7em'
  },
  priceInput: {
    height: 30
  }
}));

interface Props {
  manufacturers?: ManufacturerOption[];
}

const TableInventoryBody = ({ manufacturers }: Props) => {
  const classes = useStyles();
  const inventoryResponse = useAppSelector(
    inventorySelectors.selectInventoryResponse
  );
  const inventoryColumns = useAppSelector(
    inventorySelectors.selectInventoryColumns
  );
  const { canSeeDealersPrice } = usePermissions();

  const getItemSerialNo = useCallback((item: Inventory) => {
    if (item?.product_type === ProductTypeEnum.Consumable) {
      return '--';
    }
    if (item?.serial_no) {
      return item?.serial_no;
    }
    return '--';
  }, []);

  const onHandleCopySerialNo = (transaction?: string) => {
    copyToClipboard(transaction || '');
  };

  return (
    <TableBody>
      {inventoryResponse?.items?.map((item: Inventory, index: number) => (
        <AlternatingColorTableRow
          className={clsx(classes.tableRow, classes.tableCell)}
          hover
          key={`${index}-${item.id}`}
        >
          {/* TODO: map these in the future instead */}
          {inventoryColumns?.id && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.id || '--'}
            </TableCell>
          )}

          {inventoryColumns?.quantity && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.quantity || '--'}
            </TableCell>
          )}

          {inventoryColumns?.product_name && (
            <TableCell
              className={classes.productNameCell}
              style={{
                minWidth: (item?.product_name?.length ?? 0) >= 100 ? 790 : 500
              }}
            >
              <div className={classes.productNameText}>
                <LinkComponent
                  openInNewTab
                  href={`/app/products/${item.product_id}`}
                  title={`${item.product_name ?? '--'}`}
                />
              </div>
            </TableCell>
          )}

          {inventoryColumns?.bundle_product_name && (
            <TableCell
              className={classes.productNameCell}
              style={{
                minWidth:
                  (item?.bundle_product_name?.length ?? 0) >= 100 ? 790 : 500
              }}
            >
              <div className={classes.productNameText}>
                <LinkComponent
                  openInNewTab
                  href={`/app/products/${item.bundle_product_id}`}
                  title={`${item.bundle_product_name ?? '--'}`}
                />
              </div>
            </TableCell>
          )}

          {inventoryColumns?.sku && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.sku || '--'}
            </TableCell>
          )}

          {inventoryColumns?.category_name && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.category_name || '--'}
            </TableCell>
          )}

          {inventoryColumns?.po_no && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.po_no || '--'}
            </TableCell>
          )}

          {inventoryColumns?.dr_no && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.dr_no || '--'}
            </TableCell>
          )}

          {inventoryColumns?.serial_no && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {getItemSerialNo(item)}
              <FileCopyIcon
                style={{
                  cursor: 'pointer',
                  marginLeft: '15px',
                  fontSize: '16px'
                }}
                onClick={() => onHandleCopySerialNo(item.serial_no)}
              />
            </TableCell>
          )}

          {inventoryColumns?.status && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              <Typography
                variant="h5"
                style={{ color: getStatusColor(item?.status) }}
              >
                {item.status}
              </Typography>
              <div>
                <Typography variant="caption">
                  by {item.on_hold_by || '--'}
                </Typography>
              </div>
            </TableCell>
          )}

          {inventoryColumns?.dealers_price && canSeeDealersPrice ? (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {formatCurrency(item?.dealers_price)}
            </TableCell>
          ) : null}

          {inventoryColumns?.retail_price && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {formatCurrency(item?.retail_price)}
            </TableCell>
          )}

          {inventoryColumns?.manufacturer_id && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {manufacturerIDToName(item.manufacturer_id, manufacturers) ??
                '--'}
            </TableCell>
          )}

          {inventoryColumns?.supplier_name && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.supplier_name || '--'}
            </TableCell>
          )}

          {inventoryColumns?.branch_name && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.branch_name || '--'}
            </TableCell>
          )}

          {inventoryColumns?.encoded_by && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.encoded_by || '--'}
            </TableCell>
          )}

          {inventoryColumns?.released_by && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.released_by || '--'}
            </TableCell>
          )}

          {inventoryColumns?.transaction_no && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              <LinkComponent
                href={`/app/transaction/${item?.transaction_no}`}
                target="_blank"
                rel="noopener noreferrer"
                title={`${item?.transaction_no ?? '--'}`}
              />
            </TableCell>
          )}

          {inventoryColumns?.customer_name && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.customer_name || '--'}
            </TableCell>
          )}

          {inventoryColumns?.date_sold && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.date_sold ? formatDate(item?.date_sold) : '--'}
            </TableCell>
          )}

          {inventoryColumns?.date_purchased && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.date_purchased ? formatDate(item?.date_purchased) : '--'}
            </TableCell>
          )}

          {inventoryColumns?.date_encoded && (
            <TableCell className={clsx(classes.tableRow, classes.tableCell)}>
              {item?.date_encoded ? formatDate(item?.date_encoded) : '--'}
            </TableCell>
          )}
        </AlternatingColorTableRow>
      ))}
    </TableBody>
  );
};

export const InventoryTableBody = React.memo(TableInventoryBody);
